import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import AvatarService from "@services/AvatarService";
import markdownit from "markdown-it";

export const getSummarizingFeedback = createAsyncThunk(
  "session/summarizing-feedback",
  async ({
    source_type,
    product_id,
    scenario_ids,
    target_user_id,
    start_date,
    end_date,
  }) => {
    const response = await AvatarService.getSummarizingFeedback(
      source_type,
      product_id,
      scenario_ids,
      target_user_id,
      start_date,
      end_date,
    );

    if (response?.data?.status === "processing") {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const responseResult = await AvatarService.getSummarizingFeedbackId(
        response?.data?.id,
      );

      while (responseResult?.data?.status === "processing") {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const responseResult = await AvatarService.getSummarizingFeedbackId(
          response?.data?.id,
        );

        if (responseResult?.data?.status === "completed") {
          return responseResult.data;
        }
      }
    }

    return response.data;
  },
);

export const getSummarizingFeedbackHistory = createAsyncThunk(
  "session/summarizing-feedback-history",
  async ({id, page, size}) => {
    const response = await AvatarService.getSummarizingFeedbackHistory(
      id,
      page,
      size,
    );

    return response.data;
  },
);

export const viewSummarizingFeedback = createAsyncThunk(
  "session/view-summarizing-feedback",
  async ({id}) => {
    const response = await AvatarService.getSummarizingFeedbackId(id);

    return response.data;
  },
);

export const removeSummarizingFeedback = createAsyncThunk(
  "session/summarizing-feedback-remove",
  async ({id}) => {
    const response = await AvatarService.removeSummarizingFeedback(id);

    return response.data;
  },
);

const initialState = {
  feedbackData: [],
  totalPages: null,
  totalFeedbacks: null,
  page: 1,
  size: 5,
  error: "",
  serverStatus: "",
  feedback: "",
  fromDate: new Date().toISOString(),
  toDate: new Date().toISOString(),
  selectedValues: [],
};

const summarizationSlice = createSlice({
  name: "summarization",
  initialState,
  reducers: {
    setError(state, action) {
      state.error = action.payload;
    },
    setTotalPages(state, action) {
      state.totalPages = action.payload;
    },
    setFeedbackData(state, action) {
      state.feedbackData = action.payload;
    },
    setFeedback(state, action) {
      state.feedback = action.payload;
    },
    setServerStatus(state, action) {
      state.serverStatus = action.payload;
    },
    setFromDate(state, action) {
      state.fromDate =
        action.payload instanceof Date
          ? action.payload.toISOString()
          : action.payload;
    },
    setToDate(state, action) {
      state.toDate =
        action.payload instanceof Date
          ? action.payload.toISOString()
          : action.payload;
    },
    setSelectedValues(state, action) {
      state.selectedValues = action.payload;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setTotalFeedbacks(state, action) {
      state.totalFeedbacks = action.payload;
    },
    decrementTotalFeedbacks(state) {
      state.totalFeedbacks -= 1;
    },
    clearSummarizationState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // getSummarizingFeedbackHistory
      .addCase(getSummarizingFeedbackHistory.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(getSummarizingFeedbackHistory.fulfilled, (state, action) => {
        state.feedbackData = action.payload.data;
        state.totalFeedbacks = action.payload.count
          ? action.payload.count
          : state.totalFeedbacks;
        state.totalPages = action.payload.count
          ? Math.ceil(action.payload.count / state.size)
          : state.totalPages;
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(getSummarizingFeedbackHistory.rejected, (state) => {
        state.error = "Don`t get summarizing feedback history!";
        state.serverStatus = "error";
      })

      // getSummarizingFeedback
      .addCase(getSummarizingFeedback.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(getSummarizingFeedback.fulfilled, (state, action) => {
        const md = markdownit();
        const html = md.render(action.payload.summary || "");

        state.feedback = html;
        state.error = "";
        state.serverStatus = state.summary !== "" ? "success" : "loading";
      })
      .addCase(getSummarizingFeedback.rejected, (state) => {
        state.error = "Don`t get summarizing feedback!";
        state.serverStatus = "error";
      })

      // viewSummarizingFeedback
      .addCase(viewSummarizingFeedback.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(viewSummarizingFeedback.fulfilled, (state, action) => {
        const md = markdownit();
        const html = md.render(action.payload.summary || "");

        state.feedback = html;
        state.error = "";
        state.serverStatus = state.summary !== "" ? "success" : "loading";
      })
      .addCase(viewSummarizingFeedback.rejected, (state) => {
        state.error = "Don`t get summarizing feedback!";
        state.serverStatus = "error";
      })

      // removeSummarizingFeedback
      .addCase(removeSummarizingFeedback.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(removeSummarizingFeedback.fulfilled, (state) => {
        state.error = "";
        state.serverStatus = state.summary !== "" ? "success" : "loading";
      })
      .addCase(removeSummarizingFeedback.rejected, (state) => {
        state.error = "Don`t remove summarizing feedback!";
        state.serverStatus = "error";
      });
  },
});

export const {
  setFeedbackData,
  setError,
  setServerStatus,
  clearSummarizationState,
  setFeedback,
  setFromDate,
  setToDate,
  setSelectedValues,
  setPage,
  setTotalPages,
  decrementTotalFeedbacks,
  setTotalFeedbacks,
} = summarizationSlice.actions;
export default summarizationSlice.reducer;
